import { useState, useEffect, useCallback, useRef } from 'react';
import { usePagination } from './usePagination';

/**
 * Custom hook for managing paginated data fetching
 * Combines usePagination with data loading logic
 *
 * @param {Function} fetchFunction - Async function that fetches data (receives queryParams)
 * @param {object} options - Configuration options
 * @param {number} options.limit - Items per page (default: 20)
 * @param {object} options.filters - Additional filter parameters
 * @param {Array} options.dependencies - Dependencies to trigger reload
 * @returns {object} Data, pagination, loading state, and controls
 *
 * @example
 * const {
 *   data,
 *   loading,
 *   error,
 *   pagination,
 *   reload,
 *   goToNextPage,
 *   goToPreviousPage
 * } = usePaginatedData(
 *   async (params) => apiCall(`${API_BASE_URL}/vehicles?${params}`),
 *   { limit: 20, filters: { store_id: selectedStore } }
 * );
 */
export const usePaginatedData = (
  fetchFunction,
  options = {}
) => {
  const {
    limit = 20,
    filters = {},
    dependencies = [],
    initialOffset = 0,
  } = options;

  const {
    pagination,
    setPagination,
    goToNextPage: nextPage,
    goToPreviousPage: prevPage,
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    resetPagination,
  } = usePagination(limit);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use ref to store fetchFunction to avoid infinite loops
  // This allows us to always use the latest fetchFunction without adding it to dependencies
  const fetchFunctionRef = useRef(fetchFunction);
  useEffect(() => {
    fetchFunctionRef.current = fetchFunction;
  }, [fetchFunction]);

  const loadData = useCallback(async (pageOffset = pagination.offset) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        limit: String(pagination.limit),
        offset: String(pageOffset),
      });

      // Add filter parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.append(key, String(value));
        }
      });

      // Use the ref to get the latest fetchFunction
      const response = await fetchFunctionRef.current(queryParams.toString());

      if (response?.data) {
        setData(response.data);
        setPagination({
          limit: response.limit || pagination.limit,
          offset: response.offset || pageOffset,
          total: response.total || 0,
        });
      } else {
        setData([]);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, filters, setPagination]);

  // Track first load to apply initialOffset only once
  const isFirstLoad = useRef(true);
  const initialOffsetRef = useRef(initialOffset);

  // Load data on mount and when dependencies change
  // Note: fetchFunction is NOT in dependencies to prevent infinite loops
  useEffect(() => {
    if (isFirstLoad.current && initialOffsetRef.current > 0) {
      loadData(initialOffsetRef.current);
      isFirstLoad.current = false;
    } else {
      loadData(0);
      isFirstLoad.current = false;
    }
  }, [...dependencies, JSON.stringify(filters)]);

  // Reload from current offset (for manual refresh)
  const reload = useCallback(() => {
    loadData(pagination.offset);
  }, [loadData, pagination.offset]);

  // Pagination handlers that also load data
  const goToNextPage = useCallback(async () => {
    if (hasNextPage) {
      const nextOffset = pagination.offset + pagination.limit;
      await loadData(nextOffset);
    }
  }, [hasNextPage, pagination.offset, pagination.limit, loadData]);

  const goToPreviousPage = useCallback(async () => {
    if (hasPreviousPage) {
      const prevOffset = Math.max(0, pagination.offset - pagination.limit);
      await loadData(prevOffset);
    }
  }, [hasPreviousPage, pagination.offset, pagination.limit, loadData]);

  return {
    data,
    loading,
    error,
    pagination,
    reload,
    goToNextPage,
    goToPreviousPage,
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    resetPagination,
  };
};
